import { PhoneCall, Workflow, Mail, MessageSquare, Megaphone } from 'lucide-react';
import type { NicheConfig } from './types';

// Landing pages pod duze rynki poza Trojmiastem. Zroznicowane per miasto (inny angle,
// whyMe i FAQ) zeby NIE byly doorway pages. Model: z Trojmiasta dla calej Polski (zdalnie).

const BOOKING = 'https://calendar.app.google/4V6jKtrVUtMihmoE9';

const services = (inPlace: string): NicheConfig['services'] => ({
  sectionEyebrow: 'Co wdrażam',
  sectionTitle: 'Systemy AI, które rozwiązują konkretny problem',
  items: [
    { icon: Workflow, title: 'Custom aplikacje i SaaS', description: `Własne aplikacje szyte pod Twój proces, gdy gotowe narzędzia nie wystarczają. Dla Kognity zbudowaliśmy platformę do ofert, cenników i przetargów (integracja BaseLinker). Wdrażamy zdalnie, współpraca ${inPlace} bez problemu.` },
    { icon: PhoneCall, title: 'Voicebot AI 24/7', description: 'Recepcjonista AI odbiera telefon o każdej porze i umawia spotkania. U klienta deweloperskiego odzyskał około 35 godzin tygodniowo.' },
    { icon: MessageSquare, title: 'Obsługa klienta AI', description: 'Odpowiada na powtarzalne pytania z bazy wiedzy firmy. U klienta e-commerce 1 200 zgłoszeń miesięcznie, 95 procent bez człowieka.' },
    { icon: Mail, title: 'Cold email', description: 'Spersonalizowany outbound, który dowozi spotkania. Dla Kognity 2 000 maili dało 2 nowych klientów i kilkukrotny zwrot z inwestycji.' },
    { icon: Megaphone, title: 'Automatyzacje procesów', description: 'Spinam narzędzia (formularze, arkusze, CRM) w jeden przepływ. Koniec ręcznego przepisywania danych.' },
  ],
});

interface RegionInput {
  slug: string;
  city: string;
  inPlace: string;
  title: string;
  titleAccent: string;
  metaTitle: string;
  metaDescription: string;
  whyMeTitle: string;
  whyMeBody: string;
  faq: Array<{ q: string; a: string }>;
}

function makeRegion(i: RegionInput): NicheConfig {
  return {
    slug: i.slug,
    niche: i.city,
    meta: { title: i.metaTitle, description: i.metaDescription },
    areaServed: [i.city, 'Polska'],
    hero: {
      eyebrow: `${i.city} i cała Polska`,
      title: i.title,
      titleAccent: i.titleAccent,
      subtitle: i.metaDescription,
      ctaLabel: 'Umów 30-min rozmowę',
      ctaHref: '#booking',
    },
    services: services(i.inPlace),
    whyMe: {
      sectionEyebrow: 'Dlaczego DAIEN',
      sectionTitle: i.whyMeTitle,
      body: i.whyMeBody,
      points: [
        { title: 'Sprawdzone systemy', description: 'Każdy system najpierw chodzi w mojej własnej firmie. U klienta wdrażam to, co już działa.' },
        { title: 'Praca zdalna bez tarcia', description: `Działam z Gdyni, współpraca ${i.inPlace} idzie zdalnie: wideo, współdzielony ekran, dostępy. Efekt ten sam co na miejscu.` },
        { title: 'Konkret w liczbach', description: 'Pokazuję ile kosztuje setup, ile retainer i jaki efekt po 30, 60 i 90 dniach.' },
        { title: 'Dane zostają u Ciebie', description: 'Stack open-source-first (n8n, Retell, Supabase). Bez vendor lock-in.' },
      ],
    },
    cta: {
      title: 'Porozmawiajmy 30 minut',
      description: `Pokażę systemy, podyskutujemy o Twojej firmie ${i.inPlace}, zaproponuję rozwiązanie. Bez sprzedaży na siłę.`,
      bookingUrl: BOOKING,
    },
    faq: i.faq,
  };
}

export const regionConfigs: NicheConfig[] = [
  makeRegion({
    slug: 'systemy-ai-dla-firm-warszawa',
    city: 'Warszawa',
    inPlace: 'w Warszawie',
    title: 'Systemy AI dla firm',
    titleAccent: 'w Warszawie',
    metaTitle: 'Systemy AI i automatyzacja dla firm w Warszawie - DAIEN',
    metaDescription:
      'DAIEN buduje systemy AI i custom aplikacje dla firm w Warszawie: automatyzacja przy dużej skali operacji, voiceboty, obsługa klienta. Mniej ręcznej roboty, więcej czasu zespołu.',
    whyMeTitle: 'Skala Warszawy lubi automatyzację',
    whyMeBody:
      'Warszawskie firmy usługowe często toną w wolumenie: setki telefonów, zgłoszeń i maili dziennie. Buduję systemy, które biorą ten wolumen na siebie, żeby zespół zajmował się tym, co naprawdę wymaga człowieka. Zaczynam od Twojego wąskiego gardła i liczb, nie od technologii.',
    faq: [
      { q: 'Obsługujesz firmy z Warszawy zdalnie?', a: 'Tak. Działam z Gdyni i prowadzę wdrożenia zdalnie w całej Polsce. Spotkania online, w razie potrzeby przyjazd do Warszawy na kluczowe etapy.' },
      { q: 'Czy systemy udźwigną duży wolumen zgłoszeń?', a: 'Tak, to ich sedno. Voicebot i obsługa AI są projektowane pod skalę: u klienta e-commerce 1 200 zgłoszeń miesięcznie obsługiwane w 95 procentach bez człowieka.' },
      { q: 'Ile trwa wdrożenie?', a: 'Zwykle 2-4 tygodnie od decyzji do produkcji, zależnie od zakresu. Custom aplikacja SaaS dłużej, ustalane indywidualnie.' },
    ],
  }),
  makeRegion({
    slug: 'automatyzacja-ai-dla-firm-krakow',
    city: 'Kraków',
    inPlace: 'w Krakowie',
    title: 'Automatyzacja AI i aplikacje',
    titleAccent: 'dla firm w Krakowie',
    metaTitle: 'Automatyzacja AI i aplikacje dla firm w Krakowie - DAIEN',
    metaDescription:
      'DAIEN buduje automatyzacje i custom aplikacje AI dla firm w Krakowie: voiceboty, obsługa klienta, cold email. Zdejmuje ręczną robotę, żeby zespół robił to, co ważne.',
    whyMeTitle: 'Mniej ręcznej roboty, więcej realnej pracy',
    whyMeBody:
      'Krakowskie firmy usługowe trzymają procesy w arkuszach i mailach, przez co ludzie przepisują dane zamiast pracować merytorycznie. Buduję aplikacje i automatyzacje, które te procesy przejmują. Najpierw rozmawiamy o problemie, potem o rozwiązaniu.',
    faq: [
      { q: 'Robisz aplikacje szyte na miarę?', a: 'Tak. Dla Kognity zbudowałem całą platformę do ofert, cenników i przetargów z integracją BaseLinker. Jeśli gotowe narzędzia nie wystarczają, budujemy własne.' },
      { q: 'Współpraca z Krakowa działa zdalnie?', a: 'Tak, prowadzę wdrożenia zdalnie w całej Polsce. Wideo, współdzielony ekran, dostępy. Efekt ten sam co na miejscu.' },
      { q: 'Od czego zaczynamy?', a: 'Od 30-minutowej rozmowy o Twoim największym wąskim gardle i liczbach. Potem proponuję konkretne rozwiązanie z wyceną.' },
    ],
  }),
  makeRegion({
    slug: 'custom-software-i-ai-wroclaw',
    city: 'Wrocław',
    inPlace: 'we Wrocławiu',
    title: 'Custom software i systemy AI',
    titleAccent: 'we Wrocławiu',
    metaTitle: 'Custom software i systemy AI dla firm we Wrocławiu - DAIEN',
    metaDescription:
      'DAIEN tworzy custom software i systemy AI dla firm we Wrocławiu: własne aplikacje, automatyzacje n8n, voiceboty. Kod i dane zostają u Ciebie, bez vendor lock-in.',
    whyMeTitle: 'Własne oprogramowanie zamiast kolejnej subskrypcji',
    whyMeBody:
      'Wiele firm płaci za dziesięć narzędzi, które i tak nie robią dokładnie tego, czego trzeba. Buduję oprogramowanie szyte pod Twój proces, które je zastępuje lub spina. Kod i dane zostają u Ciebie. Zaczynam od problemu i liczb, nie od technologii.',
    faq: [
      { q: 'Czym różni się to od software house?', a: 'Łączę budowę oprogramowania z AI i automatyzacją procesów. Nie sprzedaję godzin, tylko działający system, który rozwiązuje konkretny problem biznesowy.' },
      { q: 'Czy dane zostają u mnie?', a: 'Tak. Stack open-source-first (n8n, Retell, Supabase), bez vendor lock-in. Kod i dane są Twoje.' },
      { q: 'Wrocław a praca zdalna?', a: 'Prowadzę wdrożenia zdalnie w całej Polsce, z Gdyni. Spotkania online, w razie potrzeby przyjazd na kluczowe etapy.' },
    ],
  }),
  makeRegion({
    slug: 'automatyzacja-ai-ecommerce-poznan',
    city: 'Poznań',
    inPlace: 'w Poznaniu',
    title: 'Automatyzacja AI dla firm i e-commerce',
    titleAccent: 'w Poznaniu',
    metaTitle: 'Automatyzacja AI dla firm i e-commerce w Poznaniu - DAIEN',
    metaDescription:
      'DAIEN buduje automatyzacje i obsługę klienta AI dla firm i e-commerce w Poznaniu: RAG, voiceboty, integracje. U klienta e-commerce 1 200 zgłoszeń miesięcznie, 95 procent automatycznie.',
    whyMeTitle: 'Handel i e-commerce zyskują najwięcej na AI',
    whyMeBody:
      'Firmy handlowe i sklepy w Poznaniu toną w powtarzalnych pytaniach o zamówienia, przesyłki i zwroty. Buduję obsługę klienta AI, która odpowiada sama z bazy wiedzy, i automatyzacje spinające sklep z narzędziami. Zespół zajmuje się tylko trudnymi sprawami.',
    faq: [
      { q: 'Integrujesz się z platformami e-commerce?', a: 'Tak. Robię integracje ze sklepami i narzędziami (np. BaseLinker u Kognity). Automatyzuję przepływ danych między sklepem, magazynem i obsługą.' },
      { q: 'Jak działa obsługa klienta AI?', a: 'System odpowiada na powtarzalne pytania z bazy wiedzy firmy. U klienta e-commerce z 3 spółek: 1 200 zgłoszeń miesięcznie, 95 procent bez udziału człowieka.' },
      { q: 'Poznań a współpraca zdalna?', a: 'Prowadzę wdrożenia zdalnie w całej Polsce, z Gdyni. Bez różnicy w efekcie.' },
    ],
  }),
  makeRegion({
    slug: 'systemy-ai-dla-firm-slask',
    city: 'Śląsk',
    inPlace: 'na Śląsku',
    title: 'Systemy AI i automatyzacja',
    titleAccent: 'dla firm na Śląsku',
    metaTitle: 'Systemy AI i automatyzacja dla firm na Śląsku - DAIEN',
    metaDescription:
      'DAIEN buduje systemy AI i automatyzacje dla firm usługowych i B2B na Śląsku: voiceboty, obsługa klienta, custom aplikacje. Mniej straconych zleceń, więcej czasu zespołu.',
    whyMeTitle: 'B2B na Śląsku działa na powtarzalnych procesach',
    whyMeBody:
      'Śląskie firmy usługowe i B2B tracą czas na powtarzalną ręczną robotę: telefony, oferty, przepisywanie danych. Buduję systemy, które te procesy przejmują, i custom aplikacje pod konkretny workflow. Najpierw problem i liczby, potem rozwiązanie.',
    faq: [
      { q: 'Obsługujesz firmy B2B ze Śląska?', a: 'Tak. Działam z Gdyni, wdrożenia prowadzę zdalnie w całej Polsce. Voiceboty, obsługa AI, custom aplikacje pod proces firmy.' },
      { q: 'Czy zbudujesz aplikację pod nasz proces?', a: 'Tak. Dla Kognity zbudowałem platformę do ofert, cenników i przetargów. Jeśli gotowe narzędzia nie wystarczają, budujemy własne.' },
      { q: 'Ile kosztuje wdrożenie?', a: 'Setup od kilku tysięcy złotych zależnie od zakresu, plus koszt infrastruktury. Pełna wycena po krótkim quizie kwalifikacyjnym.' },
    ],
  }),
];
